import { createFileRoute, notFound } from "@tanstack/react-router";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import {
  allArticlesQueryOptions,
  articleByHandleQueryOptions,
  productQueryOptions,
} from "@modules/shopify/hooks";
import {
  ArticleNotFound,
  BlogArticlePageView,
} from "@modules/shop/components/Blog/BlogArticlePageView";
import { articleProductHandles } from "@modules/shop/components/Blog/articleBody";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/blog/$blogHandle/$articleHandle",
)({
  component: BlogArticlePage,
  notFoundComponent: ArticleNotFoundPage,
  ssr: true,
  loader: async ({ context: { queryClient }, params }) => {
    const localeVars = getCountryVariables(params.locale);
    const articleData = await queryClient.ensureQueryData(
      articleByHandleQueryOptions({
        blogHandle: params.blogHandle,
        articleHandle: params.articleHandle,
        ...localeVars,
      }),
    );
    if (!articleData.blog?.articleByHandle) throw notFound();

    const html = articleData.blog.articleByHandle.contentHtml;
    const productHandles = articleProductHandles(
      typeof html === "string" ? html : "",
    );
    await Promise.all([
      queryClient.prefetchQuery(
        allArticlesQueryOptions({ first: 6, ...localeVars }),
      ),
      ...productHandles.map((handle) =>
        queryClient.prefetchQuery(
          productQueryOptions({ handle, ...localeVars }),
        ),
      ),
    ]);

    return articleData;
  },
  staleTime: 60_000,
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=86400",
  }),
  head: ({ params, loaderData }) => {
    const t = i18n.getFixedT(params.locale);
    const article = loaderData?.blog?.articleByHandle;
    const articleTitle = article?.title ?? params.articleHandle;
    return {
      meta: [
        ...seo({
          title:
            article?.seo?.title ||
            t("Seo:blogArticle.title", { name: articleTitle }),
          description:
            article?.seo?.description ||
            article?.excerpt ||
            t("Seo:blogArticle.description", { name: articleTitle }),
          image: article?.image?.url,
          type: "article",
        }),
      ],
    };
  },
});

function BlogArticlePage() {
  const { blogHandle, articleHandle, locale } = Route.useParams();
  return (
    <BlogArticlePageView
      blogHandle={blogHandle}
      articleHandle={articleHandle}
      locale={locale}
    />
  );
}

function ArticleNotFoundPage() {
  const { locale } = Route.useParams();
  return <ArticleNotFound locale={locale} />;
}
